import { Ordering } from "@/types/base";
import {
  BusinessReportOutcome,
  BusinessReportOutcomeResult,
  REVIEW_VERDICT_OPTIONS,
  SnapshotReviewVerdict,
  TRAFFIC_LIGHT_OPTIONS,
  TrafficLightRating,
} from "@/modules/snapshots/businessReports.types";
import { compareAsc, parseISO, isValid, startOfDay, endOfDay, isAfter, isBefore } from "date-fns";
import { getSnapshotCellDisplayText, getTypedSnapshotCellDisplayText } from "./cellText";

// Constants
const FIXED_FIELDS = ["id", "name", "status", "created_at", "verified"] as const;
const SNAPSHOT_GRID_FIXED_SORT_FIELDS = [
  { key: "name", label: "Name" },
  { key: "created_at", label: "Created Date" },
  { key: "status", label: "Status" },
  { key: "verified", label: "Verified" },
] as const;
const TRAFFIC_LIGHT_SORT_ORDER: Record<TrafficLightRating, number> = {
  RED: 0,
  AMBER: 1,
  GREEN: 2,
};
const NUMERIC_SUFFIX_MULTIPLIERS: Record<string, number> = {
  k: 1000,
  m: 1000000,
  b: 1000000000,
};

// Types
type FixedField = (typeof FIXED_FIELDS)[number];
type SortableValue = string | number | boolean | null;

export interface SnapshotGridSortField {
  key: string;
  label: string;
}

export const getSnapshotGridSortFields = (columnHeaders: string[]): SnapshotGridSortField[] => {
  const seen = new Set<string>(SNAPSHOT_GRID_FIXED_SORT_FIELDS.map(field => field.key));
  const dynamicFields: SnapshotGridSortField[] = [];

  for (const header of columnHeaders) {
    if (seen.has(header)) continue;
    seen.add(header);
    dynamicFields.push({ key: header, label: header });
  }

  return [...SNAPSHOT_GRID_FIXED_SORT_FIELDS, ...dynamicFields];
};

export const getNextSnapshotGridOrdering = (ordering: Ordering, field: string): Ordering => ({
  order_by: field,
  descending: ordering.order_by === field ? !ordering.descending : false,
});

const parseNumericSortValue = (value: string): number | null => {
  const normalized = getSnapshotCellDisplayText(value).trim();
  if (!normalized || normalized.toLowerCase() === "null") return null;

  const cleaned = normalized.replace(/[$£€¥₹,\s]/g, "");
  const match = cleaned.match(/-?\d+(?:\.\d+)?[kKmMbB]?/);
  if (!match) return null;

  const suffix = match[0].slice(-1).toLowerCase();
  const numericText = suffix in NUMERIC_SUFFIX_MULTIPLIERS ? match[0].slice(0, -1) : match[0];
  const number = Number(numericText);
  if (!Number.isFinite(number)) return null;

  return number * (NUMERIC_SUFFIX_MULTIPLIERS[suffix] ?? 1);
};

const parseStrictNumericSortValue = (value: string): number | null => {
  const normalized = getSnapshotCellDisplayText(value).trim();
  if (!normalized || normalized.toLowerCase() === "null") return null;

  const cleaned = normalized.replace(/[$£€¥₹,\s]/g, "");
  const numericTextWithSuffix = cleaned.endsWith("%") ? cleaned.slice(0, -1) : cleaned;
  if (!/^-?\d+(?:\.\d+)?[kKmMbB]?$/.test(numericTextWithSuffix)) return null;

  const suffix = numericTextWithSuffix.slice(-1).toLowerCase();
  const numericText = suffix in NUMERIC_SUFFIX_MULTIPLIERS ? numericTextWithSuffix.slice(0, -1) : numericTextWithSuffix;
  const number = Number(numericText);
  if (!Number.isFinite(number)) return null;

  return number * (NUMERIC_SUFFIX_MULTIPLIERS[suffix] ?? 1);
};

function getFieldValue(outcome: BusinessReportOutcome, field: string, isDate: boolean): SortableValue {
  if (!FIXED_FIELDS.includes(field as FixedField)) {
    if (isDate) return outcome.result.find(r => r.name === field && r.date)?.date || null;

    const cell = outcome.result.find(r => r.name === field);
    if (!cell) return null;
    if (cell.rating) return TRAFFIC_LIGHT_SORT_ORDER[cell.rating];
    if (!cell.result) return null;
    if (cell.data_type === "numeric") return parseNumericSortValue(cell.result);
    if (cell.data_type === "currency") return getTypedSnapshotCellDisplayText(cell) || null;

    const displayText = getSnapshotCellDisplayText(cell.result);
    const numericDisplayValue = parseStrictNumericSortValue(displayText);
    return numericDisplayValue ?? (displayText || null);
  }
  return (outcome as unknown as Record<string, unknown>)[field] as SortableValue;
}

function compareDates(aVal: string, bVal: string): number {
  try {
    const aDate = parseISO(aVal);
    const bDate = parseISO(bVal);

    if (isValid(aDate) && isValid(bDate)) {
      return compareAsc(aDate, bDate);
    }
    return aVal.localeCompare(bVal);
  } catch {
    return aVal.localeCompare(bVal);
  }
}

function compareValues(aVal: SortableValue, bVal: SortableValue, isDate: boolean): number {
  if (isDate && typeof aVal === "string" && typeof bVal === "string") {
    return compareDates(aVal, bVal);
  }

  if (typeof aVal === "string" && typeof bVal === "string") {
    return aVal.localeCompare(bVal);
  }

  if (typeof aVal === "number" && typeof bVal === "number") {
    return aVal - bVal;
  }

  if (typeof aVal === "boolean" && typeof bVal === "boolean") {
    return aVal === bVal ? 0 : aVal ? 1 : -1;
  }

  return String(aVal).localeCompare(String(bVal));
}

export function sortSnapshotOutcomes(
  items: BusinessReportOutcome[],
  ordering: Ordering,
  isDate: boolean,
): BusinessReportOutcome[] {
  return [...items].sort((a, b) => {
    const aVal = getFieldValue(a, ordering.order_by, isDate);
    const bVal = getFieldValue(b, ordering.order_by, isDate);

    // Handle null/undefined values
    if (aVal == null && bVal == null) return 0;

    if (isDate) {
      // For date fields, always put null values last regardless of sort order
      if (aVal == null) return 1;
      if (bVal == null) return -1;
    } else {
      // For non-date fields, use original logic
      if (aVal == null) return ordering.descending ? -1 : 1;
      if (bVal == null) return ordering.descending ? 1 : -1;
    }

    const comparison = compareValues(aVal, bVal, isDate);

    return ordering.descending ? -comparison : comparison;
  });
}

export interface DateFilter {
  startDate: Date | null;
  endDate: Date | null;
  selectedField: string | null;
}

// Helper function for date parsing
function parseDateValue(dateValue: unknown): Date | null {
  if (!dateValue) return null;

  let date: Date;
  if (typeof dateValue === "string") {
    date = dateValue.includes("T") || dateValue.includes("-") ? parseISO(dateValue) : new Date(dateValue);
  } else if (dateValue instanceof Date) {
    date = dateValue;
  } else if (typeof dateValue === "number") {
    date = new Date(dateValue);
  } else {
    return null;
  }

  return isValid(date) ? date : null;
}

// Helper function for date range checking
function isDateInRange(date: Date, startDate: Date | null, endDate: Date | null): boolean {
  const normalizedDate = startOfDay(date);

  const matchesStart = !startDate || !isAfter(startOfDay(startDate), normalizedDate);
  const matchesEnd = !endDate || !isBefore(endOfDay(endDate), normalizedDate);

  return matchesStart && matchesEnd;
}

export type ColumnKind = "text" | "date" | "rating" | "notes";

interface ExcludeNAFilterOption {
  excludeNA?: boolean;
}

interface ReviewVerdictFilterOption {
  reviewVerdicts?: SnapshotReviewVerdict[];
}

export type ColumnFilterValue =
  | ({ type: "text"; value: string } & ExcludeNAFilterOption & ReviewVerdictFilterOption)
  | ({ type: "date"; startDate: Date | null; endDate: Date | null } & ExcludeNAFilterOption & ReviewVerdictFilterOption)
  | ({ type: "rating"; ratings: TrafficLightRating[] } & ExcludeNAFilterOption & ReviewVerdictFilterOption);

export type ColumnFilters = Record<string, ColumnFilterValue | undefined>;

export interface ColumnReviewInfo {
  promptId: string;
  eligible: boolean;
  summary?: import("@/modules/snapshots/businessReports.api").SnapshotGridFieldReviewSummary | null;
}

export const isColumnFilterActive = (filter: ColumnFilterValue | undefined): boolean => {
  if (!filter) return false;
  if (filter.excludeNA === true) return true;
  if (filter.reviewVerdicts && filter.reviewVerdicts.length > 0) return true;
  if (filter.type === "text") return filter.value.trim().length > 0;
  if (filter.type === "date") return !!(filter.startDate || filter.endDate);
  return filter.ratings.length > 0;
};

interface NormalizedReviewFilterOption {
  reviewVerdicts: Set<SnapshotReviewVerdict>;
}

type NormalizedFilter =
  | ({ type: "text"; columnName: string; needle: string; excludeNA: boolean } & NormalizedReviewFilterOption)
  | ({
      type: "date";
      columnName: string;
      startDate: Date | null;
      endDate: Date | null;
      excludeNA: boolean;
    } & NormalizedReviewFilterOption)
  | ({
      type: "rating";
      columnName: string;
      ratings: Set<TrafficLightRating>;
      excludeNA: boolean;
    } & NormalizedReviewFilterOption);

const normalizeActiveFilters = (filters: ColumnFilters): NormalizedFilter[] => {
  const out: NormalizedFilter[] = [];
  for (const [columnName, filter] of Object.entries(filters)) {
    if (!filter || !isColumnFilterActive(filter)) continue;
    const reviewVerdicts = new Set(filter.reviewVerdicts ?? []);
    if (filter.type === "text") {
      out.push({
        type: "text",
        columnName,
        needle: filter.value.trim().toLowerCase(),
        excludeNA: filter.excludeNA === true,
        reviewVerdicts,
      });
    } else if (filter.type === "date") {
      out.push({
        type: "date",
        columnName,
        startDate: filter.startDate,
        endDate: filter.endDate,
        excludeNA: filter.excludeNA === true,
        reviewVerdicts,
      });
    } else {
      out.push({
        type: "rating",
        columnName,
        ratings: new Set(filter.ratings),
        excludeNA: filter.excludeNA === true,
        reviewVerdicts,
      });
    }
  }
  return out;
};

const findCellByName = (outcome: BusinessReportOutcome, columnName: string): BusinessReportOutcomeResult | undefined =>
  outcome.result?.find(cell => cell.name === columnName);

const cellMatchesFilter = (cell: BusinessReportOutcomeResult | undefined, filter: NormalizedFilter): boolean => {
  if (!cell) return false;
  if (filter.reviewVerdicts.size > 0) {
    const verdict = cell.review?.verdict;
    if (!verdict || !filter.reviewVerdicts.has(verdict)) return false;
  }
  const visibleText = getSnapshotCellDisplayText(cell.result ?? "");
  if (filter.excludeNA && visibleText.trim().toLowerCase() === "n/a") return false;
  if (filter.type === "text") {
    if (!filter.needle) return true;
    return visibleText.toLowerCase().includes(filter.needle);
  }
  if (filter.type === "date") {
    if (!filter.startDate && !filter.endDate) return true;
    const parsed = parseDateValue(cell.date);
    return parsed ? isDateInRange(parsed, filter.startDate, filter.endDate) : false;
  }
  if (filter.ratings.size === 0) return true;
  return cell.rating !== null && filter.ratings.has(cell.rating);
};

export const filterSnapshotsByColumnFilters = (
  items: BusinessReportOutcome[],
  filters: ColumnFilters,
): BusinessReportOutcome[] => {
  const active = normalizeActiveFilters(filters);
  if (active.length === 0) return items;
  return items.filter(outcome =>
    active.every(filter => cellMatchesFilter(findCellByName(outcome, filter.columnName), filter)),
  );
};

interface SerializedDateFilter {
  type: "date";
  startDate: string | null;
  endDate: string | null;
  excludeNA?: boolean;
}

type SerializedColumnFilterValue =
  | { type: "text"; value: string; excludeNA?: boolean; reviewVerdicts?: SnapshotReviewVerdict[] }
  | (SerializedDateFilter & { reviewVerdicts?: SnapshotReviewVerdict[] })
  | { type: "rating"; ratings: TrafficLightRating[]; excludeNA?: boolean; reviewVerdicts?: SnapshotReviewVerdict[] };

export const serializeColumnFilters = (filters: ColumnFilters): Record<string, SerializedColumnFilterValue> => {
  const out: Record<string, SerializedColumnFilterValue> = {};
  for (const [name, value] of Object.entries(filters)) {
    if (!value) continue;
    if (value.type === "date") {
      out[name] = {
        type: "date",
        startDate: value.startDate ? value.startDate.toISOString() : null,
        endDate: value.endDate ? value.endDate.toISOString() : null,
        excludeNA: value.excludeNA === true,
        reviewVerdicts: value.reviewVerdicts ?? [],
      };
    } else {
      out[name] = value;
    }
  }
  return out;
};

const deserializeReviewVerdicts = (raw: SnapshotReviewVerdict[] | undefined): SnapshotReviewVerdict[] => {
  if (!Array.isArray(raw)) return [];
  const allowed = new Set(REVIEW_VERDICT_OPTIONS.map(o => o.value));
  return raw.filter(v => allowed.has(v));
};

export const deserializeColumnFilters = (raw: unknown): ColumnFilters => {
  if (!raw || typeof raw !== "object") return {};
  const out: ColumnFilters = {};
  for (const [name, value] of Object.entries(raw as Record<string, SerializedColumnFilterValue>)) {
    if (!value || typeof value !== "object") continue;
    const reviewVerdicts = deserializeReviewVerdicts(value.reviewVerdicts);
    if (value.type === "text" && typeof value.value === "string") {
      out[name] = { type: "text", value: value.value, excludeNA: value.excludeNA === true, reviewVerdicts };
    } else if (value.type === "date") {
      const startDate = value.startDate ? parseDateValue(value.startDate) : null;
      const endDate = value.endDate ? parseDateValue(value.endDate) : null;
      out[name] = { type: "date", startDate, endDate, excludeNA: value.excludeNA === true, reviewVerdicts };
    } else if (value.type === "rating" && Array.isArray(value.ratings)) {
      const allowed = new Set(TRAFFIC_LIGHT_OPTIONS.map(o => o.value));
      out[name] = {
        type: "rating",
        ratings: value.ratings.filter(r => allowed.has(r)),
        excludeNA: value.excludeNA === true,
        reviewVerdicts,
      };
    }
  }
  return out;
};
